import React, { useEffect, useMemo, useState } from 'react';

import { FilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { getter, setter } from '@progress/kendo-react-common';
import {
  MultiSelectTree,
  MultiSelectTreeChangeEvent,
  MultiSelectTreeExpandEvent,
  MultiSelectTreeFilterChangeEvent,
  getMultiSelectTreeValue,
} from '@progress/kendo-react-dropdowns';

import { TagModel } from 'models';

import { useEvent } from 'core/hooks';

import './TagsField.css';

// --- Kendo Helper Functions (adapted from user's sample) ---
const getValueMap = (value: TagModel[] | undefined, idGetter: (item: TagModel) => number): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  if (value?.length) {
    value.forEach((item: TagModel) => {
      const key = idGetter(item);
      map[String(key)] = true;
    });
  }
  return map;
};

const expandedState = (item: TagModel, dataItemKey: string, currentExpandedIds: number[]): number[] => {
  const nextExpanded = currentExpandedIds.slice();
  const keyGetter = getter(dataItemKey);
  const itemKey = keyGetter(item) as number;
  const index = nextExpanded.findIndex((currentKey) => currentKey === itemKey);
  if (index === -1) {
    nextExpanded.push(itemKey);
  } else {
    nextExpanded.splice(index, 1);
  }
  return nextExpanded;
};

// Internal helper for processMultiSelectTreeData
const mapMultiSelectTreeData = (
  data: TagModel[] | undefined,
  options: {
    keyGetter: (item: TagModel) => number;
    subItemGetter: (item: TagModel) => TagModel[] | undefined;
    subItemSetter: (item: TagModel, children: TagModel[]) => void;
    checkSetter: (item: TagModel, value: boolean) => void;
    expandedSetter: (item: TagModel, value: boolean) => void;
    checkIndeterminateSetter: (item: TagModel, value: boolean) => void;
    valueMap: Record<string, boolean>;
    expandedMap: Record<string, boolean>;
  },
): [TagModel[], boolean] => {
  const { keyGetter, subItemGetter, subItemSetter, checkSetter, expandedSetter, checkIndeterminateSetter, valueMap, expandedMap } = options;

  if (!data?.length) {
    return [data || [], false];
  }

  let hasChecked = false;
  const newData = [...data].map((dataItem) => {
    const [children, hasCheckedChildren] = mapMultiSelectTreeData(subItemGetter(dataItem), options);
    const itemKey = keyGetter(dataItem);
    const isChecked = !!valueMap[String(itemKey)];
    if (isChecked || hasCheckedChildren) {
      hasChecked = true;
    }

    const newItem = { ...dataItem };
    const currentItemKey = keyGetter(newItem);

    expandedSetter(newItem, !!expandedMap[String(currentItemKey)]);
    subItemSetter(newItem, children);
    checkSetter(newItem, isChecked);
    checkIndeterminateSetter(newItem, !isChecked && hasCheckedChildren);

    return newItem;
  });

  return [newData, hasChecked];
};

const processMultiSelectTreeData = (
  tree: TagModel[] | undefined,
  options: {
    subItemsField: string;
    checkField: string;
    checkIndeterminateField: string;
    expandField: string;
    dataItemKey: string;
    value: TagModel[] | undefined;
    filter: FilterDescriptor | null;
    expandedIds: number[];
  },
): TagModel[] => {
  const { subItemsField, checkField, checkIndeterminateField, expandField, dataItemKey, value, filter, expandedIds } = options;

  const keyGetter = getter(dataItemKey) as (item: TagModel) => number;
  const expandedMap: Record<string, boolean> = {};
  expandedIds.forEach((id: number) => (expandedMap[String(id)] = true));

  const [result] = mapMultiSelectTreeData(tree, {
    valueMap: getValueMap(value, keyGetter),
    expandedMap: expandedMap,
    keyGetter,
    expandedSetter: setter(expandField),
    subItemGetter: getter(subItemsField) as (item: TagModel) => TagModel[] | undefined,
    subItemSetter: setter(subItemsField) as (item: TagModel, children: TagModel[]) => void,
    checkSetter: setter(checkField) as (item: TagModel, value: boolean) => void,
    checkIndeterminateSetter: setter(checkIndeterminateField) as (item: TagModel, value: boolean) => void,
  });

  return filter?.value && filter ? filterBy(result, filter) : result;
};
// --- End Kendo Helper Functions ---

export interface TagsFieldProps {
  tags?: TagModel[];
  value?: TagModel[];
  onChange?: (newValue: TagModel[]) => void;
  initialExpandedIds?: number[];
  id?: string;
  label?: string;
  placeholder?: string;
}

// Define field name constants (aligning with TagModel and Kendo sample logic)
const DATA_ITEM_KEY = 'id';
const TEXT_FIELD = 'name'; // Tells MultiSelectTree to display TagModel.name
const SUB_ITEMS_FIELD = 'children'; // Tells MultiSelectTree to use TagModel.children

// These are the names of the *new fields* that processMultiSelectTreeData will add to items
const CHECK_FIELD = 'selectedState';
const CHECK_INDETERMINATE_FIELD = 'indeterminateState';
const EXPAND_FIELD = 'expandedState';

export const TagsField: React.FC<TagsFieldProps> = ({
  tags: initialTags,
  value: controlledValue,
  onChange: onControlledChange,
  initialExpandedIds,
  id,
  label = 'Select Tags',
  placeholder = 'Search and select tags...',
}) => {
  const [currentValue, setCurrentValue] = useState<TagModel[]>(controlledValue || []);
  const [expandedIds, setExpandedIds] = useState<number[]>(() => {
    if (initialExpandedIds) return initialExpandedIds;
    const firstTag = initialTags?.[0];
    if (firstTag && typeof firstTag[DATA_ITEM_KEY as keyof TagModel] === 'number') {
      return [firstTag[DATA_ITEM_KEY as keyof TagModel] as number];
    }
    return [];
  });
  const [filterDescriptor, setFilterDescriptor] = useState<FilterDescriptor | null>(null);

  // Effect to update internal state if controlled props change
  useEffect(() => {
    if (controlledValue !== undefined) {
      setCurrentValue(controlledValue);
    }
  }, [controlledValue]);

  const handleOnChange = useEvent((event: MultiSelectTreeChangeEvent) => {
    const newValue = getMultiSelectTreeValue(initialTags || [], {
      dataItemKey: DATA_ITEM_KEY,
      subItemsField: SUB_ITEMS_FIELD,
      items: event.items as TagModel[],
      operation: event.operation,
      value: currentValue,
    }) as TagModel[];

    if (controlledValue === undefined) {
      setCurrentValue(newValue);
    }
    if (onControlledChange) {
      onControlledChange(newValue);
    }
  });

  const handleOnExpandChange = useEvent((event: MultiSelectTreeExpandEvent) => {
    if (event.item && typeof (event.item as TagModel)[DATA_ITEM_KEY as keyof TagModel] === 'number') {
      setExpandedIds(expandedState(event.item as TagModel, DATA_ITEM_KEY, expandedIds));
    }
  });

  const handleOnFilterChange = useEvent((event: MultiSelectTreeFilterChangeEvent) => {
    setFilterDescriptor(event.filter);
  });

  const treeDataForKendo = useMemo(() => {
    return processMultiSelectTreeData(initialTags, {
      expandedIds,
      value: currentValue,
      filter: filterDescriptor,
      dataItemKey: DATA_ITEM_KEY,
      checkField: CHECK_FIELD,
      checkIndeterminateField: CHECK_INDETERMINATE_FIELD,
      expandField: EXPAND_FIELD,
      subItemsField: SUB_ITEMS_FIELD,
    });
  }, [initialTags, expandedIds, currentValue, filterDescriptor]);

  return (
    <div className="tags-field-container">
      <MultiSelectTree
        id={id}
        label={label}
        placeholder={placeholder}
        className="tags-multiselecttree"
        data={treeDataForKendo}
        value={currentValue}
        onChange={handleOnChange}
        textField={TEXT_FIELD}
        dataItemKey={DATA_ITEM_KEY}
        subItemsField={SUB_ITEMS_FIELD}
        checkField={CHECK_FIELD}
        checkIndeterminateField={CHECK_INDETERMINATE_FIELD}
        expandField={EXPAND_FIELD}
        onExpandChange={handleOnExpandChange}
        filterable={true}
        onFilterChange={handleOnFilterChange}
      />
    </div>
  );
};

export default TagsField;
